import React, { useState } from 'react';
import { X, Calendar, Clock, MapPin, Trophy, Shield, Info } from 'lucide-react';

const AddUmpireAssignmentModal = ({ isOpen, onClose, teams, onAddAssignment }) => {
    const [homeTeam, setHomeTeam] = useState('');
    const [awayTeam, setAwayTeam] = useState('');
    const [venue, setVenue] = useState('');
    const [matchType, setMatchType] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [umpiringTeamId, setUmpiringTeamId] = useState('');
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Basic validation
        if (!homeTeam.trim() || !awayTeam.trim()) {
            setError('Please enter both Home and Away teams.');
            return;
        }
        if (!date) {
            setError('Please select a date.');
            return;
        }
        if (!time) {
            setError('Please select a time.');
            return;
        }
        if (!umpiringTeamId) {
            setError('Please assign an umpiring team.');
            return;
        }

        const selectedTeam = teams.find(t => t.id === umpiringTeamId);
        if (!selectedTeam) {
            setError('Selected team is invalid.');
            return;
        }

        const newAssignment = {
            homeTeam: homeTeam.trim(),
            awayTeam: awayTeam.trim(),
            venue: venue.trim(),
            matchType: matchType.trim() || 'League', // default to League if blank
            date,
            time,
            umpiringTeamId,
            umpiringTeamName: selectedTeam.name,
            volunteers: [] // Initialize empty volunteers array
        };

        onAddAssignment(newAssignment);
        
        // Reset and close
        setHomeTeam('');
        setAwayTeam('');
        setVenue('');
        setMatchType('');
        setDate('');
        setTime('');
        setUmpiringTeamId('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-purple-50">
                    <div className="flex items-center gap-2">
                        <Trophy className="text-purple-600 w-5 h-5" />
                        <h2 className="text-xl font-bold text-gray-800">Add Umpiring Assignment</h2>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        type="button"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
                    <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                        {error && (
                            <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm border border-red-100">
                                <Info size={16} />
                                {error}
                            </div>
                        )}

                        {/* Match info (Home vs Away) */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                    Home Team *
                                </label>
                                <input
                                    type="text"
                                    value={homeTeam}
                                    onChange={(e) => setHomeTeam(e.target.value)}
                                    placeholder="e.g. MSCC Centurions"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                    Away Team *
                                </label>
                                <input
                                    type="text"
                                    value={awayTeam}
                                    onChange={(e) => setAwayTeam(e.target.value)}
                                    placeholder="e.g. OCS The Red Giants"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                    required
                                />
                            </div>
                        </div>

                        {/* Match Type and Venue */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                    Match Type / Stage
                                </label>
                                <input
                                    type="text"
                                    value={matchType}
                                    onChange={(e) => setMatchType(e.target.value)}
                                    placeholder="e.g. League, Playoff"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                    Venue
                                </label>
                                <input
                                    type="text"
                                    value={venue}
                                    onChange={(e) => setVenue(e.target.value)}
                                    placeholder="e.g. Marymoor Park"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                />
                            </div>
                        </div>

                        {/* Date and Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <Calendar size={12} /> Date *
                                </label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <Clock size={12} /> Time *
                                </label>
                                <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                    required
                                />
                            </div>
                        </div>

                        {/* Assigned Umpiring Team */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1 text-purple-700 font-bold">
                                <Shield size={12} /> Umpiring Team *
                            </label>
                            <select
                                value={umpiringTeamId}
                                onChange={(e) => setUmpiringTeamId(e.target.value)}
                                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                                required
                            >
                                <option value="">Select Club Team...</option>
                                {teams.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors text-sm shadow-sm"
                        >
                            Add Assignment
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddUmpireAssignmentModal;
